import React, { useContext, useMemo, useState } from 'react';
import {
  IObjektVy,
  IObjektVyComp,
  IBusinessCollectionProps,
} from '../../../../../../../Types/ObjektVy';
import EDView from '../../../../../../../BaseComponents/View/EDView';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import FakturaDelContext from '../../../FakturaDel/Components/FakturaDelContextManager/FakturaDelContext';
import { LoginStateContext } from '../../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import {
  FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All,
  FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All,
} from '../../../FakturaDel/Queries/_QueryTypes/FakturaDelType';
import AttestMessageContext from '../../../FakturaDel/Components/AttestMessageContext/AttestMessageContext';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All } from '../../../../../Ekonomi/Rutiner/EkonomiskAnalys/Components/VerifikationModal/Queries/VerifikationQuery/_QueryTypes/Verifikationdetails';
import { AUTOMATICALLY_ADDED_VALUE_PREFIX } from '../../../FakturaDel/Components/FakturaDelContextManager/FakturaDelContextManager';
import FakturaAttestMessage from './Components/FakturaAttestMessage/FakturaAttestMessage';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import {
  LR_KVITTERA_ENDAST_RIKTADE,
  LR_USE_KVITTERING,
} from '../../../FakturaDel/Consts/FakturaDelSysoptionConsts';
import { FakturaattestWrapperContext } from '../../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import { LRSysOptionsQuery_db_SYSOptions_All } from '../../../FakturaDel/Queries/LRSysOptionQuery/_QueryTypes/LRSysOptionsQuery';
import { shouldUserKvitteraMessage } from '../../Utils/AttestMeddelandeUtils';

interface IProps extends IBusinessCollectionProps {
  messages:
    | FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All[]
    | Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All[];
}

const AttestMeddelandeComp: IObjektVyComp = {
  caption: '',
  comp: ((props: IProps) =>
    <AttestMeddelandeView {...props} />) as IObjektVyComp['comp'],
};

// Denna tooltip komponenten kan vi använda oss utav om vi vill göra det möjligt att se vilka som har kvitterat meddelandet
// interface ITooltipProps {
//   tooltip: string;
//   sysOptionActive: boolean;
// }
// const Tooltip: React.FC<ITooltipProps> = props => {
//   if (props.sysOptionActive) {
//     return (
//       <EDTooltip enterDelay={500} enterTouchDelay={500} title={props.tooltip}>
//         <span>{props.children}</span>
//       </EDTooltip>
//     );
//   }
//   return <>{props.children}</>;
// };

export const AttestMeddelandeView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const fakturaDelContext = useContext(FakturaDelContext);
  const formContext = useContext(BusinessDataFormContext);
  const loginStateContext = useContext(LoginStateContext);
  const attestMessageContext = useContext(AttestMessageContext);
  const fakturaattestContext = useContext(FakturaattestWrapperContext);
  const [originalValue, setOriginalValue] = useState('');

  const hasMessageProp = Boolean(props.messages);

  const sysOptionActive: boolean = useMemo(() => {
    return hasMessageProp
      ? false
      : fakturaattestContext.sysOptions?.find(
          (option: LRSysOptionsQuery_db_SYSOptions_All) =>
            option.OptionKey === LR_USE_KVITTERING
        )?.OptionData === 'Y';
  }, [fakturaattestContext.sysOptions, hasMessageProp]);

  const onlyKvitteraDirectedMessages: boolean = useMemo(() => {
    return hasMessageProp
      ? false
      : fakturaattestContext.sysOptions?.find(
          (option: LRSysOptionsQuery_db_SYSOptions_All) =>
            option.OptionKey === LR_KVITTERA_ENDAST_RIKTADE
        )?.OptionData === 'Y';
  }, [fakturaattestContext.sysOptions, hasMessageProp]);

  const savedMessages = useMemo(
    () =>
      props.messages
        ? props.messages
        : (fakturaDelContext.attestMessages ?? [])
            .filter(
              (
                value: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
              ) => !value.id.startsWith(AUTOMATICALLY_ADDED_VALUE_PREFIX)
            )
            .filter(
              (
                message: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
              ) =>
                !formContext?.formState.values?._Associations?.Faktura?._Associations?.Attestmeddelande_Delete_Values?.some(
                  (deletedMessage: any) => deletedMessage.id === message.id
                )
            ),
    [
      fakturaDelContext.attestMessages,
      props.messages,
      formContext?.formState.values?._Associations?.Faktura?._Associations
        ?.Attestmeddelande_Delete_Values,
    ]
  );

  const newlyCreatedMessages: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All[] =
    useMemo(
      () =>
        (fakturaDelContext.attestMessages ?? []).filter(
          (
            value: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
          ) => value.id.startsWith(AUTOMATICALLY_ADDED_VALUE_PREFIX)
        ),
      [fakturaDelContext.attestMessages]
    );

  const isLastSender = useMemo(
    () =>
      props.messages
        ? false
        : (savedMessages?.[0]?.AvsandareSignatur?.toLocaleLowerCase() ?? '') ===
          loginStateContext.sign.toLocaleLowerCase(),
    [loginStateContext.sign, props.messages, savedMessages]
  );

  const attesterar = fakturaDelContext.jagAttesterar;

  // Lämnar detta här utifall vi vill visa vilka som har kvitterat i framtiden så kan man använda sig utav denna funktion
  // const getKvittensLabel = useCallback(
  //   (
  //     prev: string,
  //     curr: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All,
  //     index
  //   ) => {
  //     const newlyAdded = curr.id.startsWith(AUTOMATICALLY_ADDED_VALUE_PREFIX);
  //     let reservText = '';
  //     const sign = newlyAdded
  //       ? fakturaDelContext.minFakturaAttest.aktuellFakturaAttest?._Associations
  //           ?.Attestratt?._Associations?.Attestant?._Associations?.Anvandare
  //           ?.SIGNATURE
  //       : curr._Associations?.Attestratt?._Associations?.Attestant
  //           ?._Associations?.Anvandare?.SIGNATURE;
  //     const kvitteratAv = newlyAdded
  //       ? fakturaDelContext.minFakturaAttest.aktuellFakturaAttest?._Associations
  //           ?.Attestratt?._Associations?.Attestant?.BENAMNING
  //       : curr._Associations?.Attestratt?._Associations?.Attestant?.BENAMNING;
  //     const reserv = curr._Associations?.ReservAttestratt?.id;
  //     if (reserv) {
  //       reservText = `${
  //         newlyAdded
  //           ? loginStateContext.name
  //           : curr._Associations?.ReservAttestratt?._Associations?.Attestant
  //               ?.BENAMNING
  //       } (reserv för ${kvitteratAv})`;
  //     }
  //     if (index === 0) {
  //       return `Kvitterat av ${reserv ? reservText : kvitteratAv} ${
  //         loginStateContext.sign.toLocaleLowerCase() ===
  //         sign?.toLocaleLowerCase()
  //           ? '(' + curr.DATUM + ')'
  //           : ''
  //       }`;
  //     }
  //     return `${prev}, ${reserv ? reservText : kvitteratAv} ${
  //       loginStateContext.sign.toLocaleLowerCase() === sign?.toLocaleLowerCase()
  //         ? '(' + curr.DATUM + ')'
  //         : ''
  //     }`;
  //   },
  //   [fakturaDelContext, loginStateContext]
  // );

  return (
    <EDBox sx={{ width: '100%' }}>
      {newlyCreatedMessages.length > 0 &&
        newlyCreatedMessages.map((message, index) => {
          const isEditMode =
            attestMessageContext.messageEditModeID === message.id;

          const showEditButtons = !isEditMode && attesterar;

          const isSender = true;

          const kvittens:
            | FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All
            | undefined = undefined;

          const isReceiver = false;

          const showKvittens = false;

          const deleteOrUndoMessage = (
            event: React.MouseEvent<HTMLElement, MouseEvent>
          ) => {
            event.preventDefault();
            event.stopPropagation();
            attestMessageContext.setMessageEditModeID('');
            fakturaDelContext.deleteOrUndoAttestMessage(message.id);
            setOriginalValue('');
          };

          return (
            <FakturaAttestMessage
              key={index}
              deleteOrUndoMessage={deleteOrUndoMessage}
              deleteOrUndoMessageTooltip={'Radera meddelande'}
              hasChanged={false}
              hasMessageProp={hasMessageProp}
              index={index}
              isEditMode={isEditMode}
              message={message}
              setOriginalValue={setOriginalValue}
              showEditButtons={showEditButtons}
              showKvittensButton={showKvittens && isReceiver}
              sysOptionActive={sysOptionActive}
              kvittens={kvittens}
              hasKvitterat={
                (kvittens && attesterar && !isSender && isReceiver) ?? false
              }
            />
          );
        })}

      {savedMessages.length > 0 &&
        savedMessages.map((message, index) => {
          const isEditMode =
            attestMessageContext.messageEditModeID === message.id;

          const isSender =
            (message?._Associations?.AvsandarAttestratt?.id ?? '') ===
              fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
                ?._Associations?.Attestratt?.id ||
            (message?.AvsandareSignatur?.toLocaleLowerCase() ?? '') ===
              loginStateContext.sign.toLocaleLowerCase();

          const hasChanged = (message.MEDDELANDE ?? '') !== originalValue;

          const kvittenser: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All[] =
            (message._Associations as any)?.Kvittenser;

          const kvittens:
            | FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All
            | undefined = kvittenser?.find(
            (
              kvittens: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All
            ) => {
              return (
                kvittens._Associations?.Attestratt?.id ===
                fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
                  ?._Associations?.Attestratt?.id
              );
            }
          );

          const needsToKvitteraMessage = shouldUserKvitteraMessage(
            message,
            fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
              ?._Associations?.Attestratt?.id,
            onlyKvitteraDirectedMessages
          );

          const showKvittens =
            !kvittens &&
            !isSender &&
            attesterar &&
            (isEditMode ? false : true) &&
            !formContext?.formState.loading &&
            needsToKvitteraMessage;

          const deleteOrUndoMessage = (
            event: React.MouseEvent<HTMLElement, MouseEvent>
          ) => {
            event.stopPropagation();
            if (attestMessageContext.messageEditModeID === '') {
              fakturaDelContext.deleteOrUndoAttestMessage(message.id);
            } else {
              if (originalValue !== message.MEDDELANDE) {
                fakturaDelContext.deleteOrUndoAttestMessage(
                  message.id,
                  originalValue
                );
              }
              attestMessageContext.setMessageEditModeID('');
            }
            setOriginalValue('');
          };

          const showEditButtons =
            isLastSender &&
            index === 0 &&
            !isEditMode &&
            attesterar &&
            !hasMessageProp;

          return (
            <FakturaAttestMessage
              key={index}
              deleteOrUndoMessage={deleteOrUndoMessage}
              deleteOrUndoMessageTooltip={'Ångra ändring'}
              hasChanged={hasChanged}
              hasMessageProp={hasMessageProp}
              index={index}
              isEditMode={isEditMode}
              message={message}
              setOriginalValue={setOriginalValue}
              showEditButtons={showEditButtons}
              showKvittensButton={showKvittens}
              sysOptionActive={sysOptionActive}
              kvittens={kvittens}
              hasKvitterat={
                (kvittens &&
                  attesterar &&
                  !isSender &&
                  needsToKvitteraMessage) ??
                false
              }
            />
          );
        })}
      {savedMessages.length === 0 && newlyCreatedMessages.length === 0 && (
        <EDView gutter>
          <EDTypography>Det finns inga meddelanden att visa här</EDTypography>
        </EDView>
      )}
    </EDBox>
  );
};

export const AttestMeddelande: IObjektVy = {
  type: 'ObjektVy',
  id: 'AttestMeddelande',
  info: {},
  def: () => AttestMeddelandeComp,
};
